import i18next from 'i18next';
import moment from 'moment-timezone';
import React from 'react';

import theme from '@theme';

import { DATE_DISPLAY_FORMATS } from '@commons/DatePickers/constants';
import { Label } from '@commons/utils/styledLibraryComponents';
import { renderTargetProductionWasteRate } from '@commons/ListViewColumns';
import DisplayNumber from '@commons/DisplayNumber';
import Text, { ENUM_COLORS, ENUM_FONTS } from '@commons/Text';

import { LabelContainer } from '../styledComponents';

const _getServiceColumns = (hasMultipleService) => {
  if (hasMultipleService) {
    return [
      {
        id: 'minNoon',
        propertyKey: 'minNoon',
        filterType: 'numeric',
        excludeFromSearch: true,
        name: i18next.t('ADMIN.STORES.MINIMUM_COLUMN_NOON'),
        render: (item) => <DisplayNumber number={item} withoutDecimals />,
      },
      {
        id: 'minEvening',
        propertyKey: 'minEvening',
        filterType: 'numeric',
        excludeFromSearch: true,
        name: i18next.t('ADMIN.STORES.MINIMUM_COLUMN_EVENING'),
        render: (item) => <DisplayNumber number={item} withoutDecimals />,
      },
    ];
  }

  return [
    {
      id: 'min',
      propertyKey: 'min',
      filterType: 'numeric',
      excludeFromSearch: true,
      name: i18next.t('ADMIN.STORES.MINIMUM_COLUMN'),
      render: (item) => <DisplayNumber number={item} withoutDecimals />,
    },
  ];
};

export const getColumns = (hasMultipleServices, useProductsTargetProductionWasteRate) => {
  const columns = [
    {
      id: 'name',
      propertyKey: 'name',
      name: i18next.t('GENERAL.PRODUCT'),
      render: (item) => (
        <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_MIDDLE_BOLD}>
          {item}
        </Text>
      ),
    },
    {
      id: 'category',
      propertyKey: 'category',
      name: i18next.t('ADMIN.STORES.LIST_COLUMN_CATEGORY'),
      filterType: 'string',
      filterStringAllowNone: true,
      render: (item) => (
        <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_BIG}>
          {item || '-'}
        </Text>
      ),
    },
    {
      id: 'subCategory',
      propertyKey: 'subCategory',
      name: i18next.t('GENERAL.SUB_CATEGORY'),
      filterType: 'string',
      filterStringAllowNone: true,
      render: (item) => (
        <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_BIG}>
          {item || '-'}
        </Text>
      ),
    },
    {
      id: 'price',
      type: 'currency',
      propertyKey: 'price',
      name: i18next.t('ADMIN.STORES.LIST_COLUMN_PRICE_TAX_INCLUDED'),
      filterType: 'numeric',
      excludeFromSearch: true,
      render: (item) => <DisplayNumber number={item} displayCurrencyCode />,
    },
    {
      id: 'launchDate',
      propertyKey: 'launchDate',
      name: i18next.t('ADMIN.STORES.LIST_COLUMN_LAUNCH_DATE'),
      filterType: 'date',
      excludeFromSearch: true,
      render: (item) => (
        <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_BIG}>
          {item
            ? moment(item).format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR)
            : i18next.t('GENERAL.SELECT_NONE_FEMININE')}
        </Text>
      ),
    },
    {
      id: 'endDate',
      propertyKey: 'endDate',
      name: i18next.t('ADMIN.STORES.LIST_COLUMN_END_DATE'),
      filterType: 'date',
      excludeFromSearch: true,
      render: (item) => (
        <Text color={ENUM_COLORS.DARKEST} font={ENUM_FONTS.TEXT_BIG}>
          {item
            ? moment(item).format(DATE_DISPLAY_FORMATS.SLASHED_DAY_MONTH_YEAR)
            : i18next.t('GENERAL.SELECT_NONE_FEMININE')}
        </Text>
      ),
    },
    {
      id: 'hasStock',
      propertyKey: 'hasStock',
      name: i18next.t('ADMIN.STORES.STOCKS_COLUMN'),
      filterType: 'boolean',
      excludeFromSearch: true,
      list: [
        {
          id: 0,
          itemValue: true,
          name: i18next.t('GENERAL.YES'),
          value: i18next.t('GENERAL.YES'),
        },
        {
          id: 1,
          itemValue: false,
          name: i18next.t('GENERAL.NO'),
          value: i18next.t('GENERAL.NO'),
        },
      ],
      render: (item) => (
        <LabelContainer>
          <Label
            background={item ? theme.colors.infoGreen : theme.colors.infoRed}
            color={item ? theme.colors.infoGreen : theme.colors.infoRed}
            type={null}
            width={'40px'}
          >
            {i18next.t(item ? 'GENERAL.YES' : 'GENERAL.NO')}
          </Label>
        </LabelContainer>
      ),
    },
    ..._getServiceColumns(hasMultipleServices),
  ];

  if (useProductsTargetProductionWasteRate) {
    columns.push({
      id: 'targetProductionWasteRate',
      type: 'number',
      propertyKey: 'targetProductionWasteRate',
      name: i18next.t('BACKOFFICE.STORES.CREATION_MODAL_CARD_PRODUCTION_PROD_WASTE_RATE'),
      filterType: 'numeric',
      excludeFromSearch: true,
      render: renderTargetProductionWasteRate,
    });
  }

  return columns;
};
